import { Button, Modal } from "react-bootstrap"
import { useState } from "react"
import QuickForm from "../../Forms/QuickForm/QuickForm"
import { FieldGeneratorFieldType } from "../../FormFields/FieldGenerator/FieldGenerator"
import './quickSelectModal.scss'

export interface QuickSelectOption {
    id: string
    label: string
    secondaryLabel?: string
    data?: any
}

export default function QuickSelectModal(props: {
    active: boolean
    onClose: () => void
    onSelect: (selected: string | string[]) => void
    options: QuickSelectOption[]
    title?: string
    message?: string
    multiSelect?: boolean
    isLoading?: boolean
    disabled?: boolean
    filterPlaceholder?: string
    filterFields?: FieldGeneratorFieldType[]
    initialFilterValues?: any
    filterFn?: (option: QuickSelectOption, filterValues: any) => boolean
}) {

    const {
        active,
        onClose,
        onSelect,
        options,
        title = "Select",
        message = "Please select an option",
        multiSelect = false,
        isLoading = false,
        disabled = false,
        filterPlaceholder = "Filter",
        filterFields = [],
        initialFilterValues = {},
        filterFn,
    } = props

    const [filterValues, setFilterValues] = useState<any>({ quickFilter: '', ...initialFilterValues })
    const [selectedId, setSelectedId] = useState('')
    const [selectedIds, setSelectedIds] = useState<string[]>([])

    const defaultFilterFn = (option: QuickSelectOption, values: any) => {
        const search = (values.quickFilter || '').toLowerCase()
        if (!search) return true
        return option.label.toLowerCase().includes(search) ||
            (option.secondaryLabel?.toLowerCase().includes(search) ?? false)
    }

    const filteredOptions = options.filter((option) => (filterFn ?? defaultFilterFn)(option, filterValues))

    const toggleSelection = (id: string) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter(sid => sid !== id))
        } else {
            setSelectedIds([...selectedIds, id])
        }
    }

    const handleConfirm = () => {
        if (multiSelect) {
            onSelect(selectedIds)
        } else if (selectedId) {
            onSelect(selectedId)
        }
    }

    return (
        <Modal
            show={active}
            onHide={() => {
                if (disabled) return
                onClose()
            }}
        >
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-lg font-semibold mb-4">{message}</p>

                <QuickForm
                    initialData={{ quickFilter: '', ...initialFilterValues }}
                    fields={[
                        {
                            fieldName: 'quickFilter',
                            fieldLabel: filterPlaceholder,
                            fieldType: 'text',
                            placeholder: filterPlaceholder,
                        },
                        ...filterFields,
                    ]}
                    onChange={(e: any) => {
                        const fieldName = e.target?.name
                        if (!fieldName) return
                        const fieldValue = e.target.type === 'checkbox' ? e.target.checked : e.target.value
                        setFilterValues((prev: any) => ({ ...prev, [fieldName]: fieldValue }))
                    }}
                />

                {isLoading && <p>Loading...</p>}

                {!isLoading && filteredOptions.length === 0 && (
                    <p>No options found</p>
                )}

                {!isLoading && filteredOptions.length > 0 && (
                    <div className="quick-select-list-container">
                        {filteredOptions.map((option) => {
                            const isSelected = multiSelect
                                ? selectedIds.includes(option.id)
                                : selectedId === option.id
                            return (
                                <div
                                    key={option.id}
                                    className={`quick-select-entry ${isSelected ? 'selected' : ''}`}
                                    onClick={() => {
                                        if (multiSelect) {
                                            toggleSelection(option.id)
                                        } else {
                                            setSelectedId(option.id)
                                        }
                                    }}
                                >
                                    {multiSelect && (
                                        <span className="me-2">{isSelected ? '☑' : '☐'}</span>
                                    )}
                                    {option.label}
                                    {option.secondaryLabel && (
                                        <span className="ms-2 text-muted small">{option.secondaryLabel}</span>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="primary"
                    disabled={multiSelect ? selectedIds.length === 0 || disabled : !selectedId || disabled}
                    onClick={handleConfirm}
                >
                    {multiSelect ? `Select (${selectedIds.length})` : 'Select'}
                </Button>
                <Button variant="secondary" onClick={onClose} disabled={disabled}>
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
